import { BACKEND_URL } from "@/lib/api";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Activity, ShieldCheck, Cpu, Network } from "lucide-react";

const BACKEND = BACKEND_URL;

function useCountUp(target, duration = 900) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!target && target !== 0) return;
    const start = performance.now();
    const from = 0;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.floor(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return v;
}

function format(n) {
  if (n == null) return "—";
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return n.toLocaleString();
}

export default function TrustBanner() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/public/stats`);
        if (!cancelled) setStats(r.data);
      } catch (_) {
        /* silent — banner is purely informational */
      }
    };
    load();
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const leads24h = useCountUp(stats?.leads_last_24h ?? 0);
  const leadsTotal = useCountUp(stats?.leads_total ?? 0);
  const qualified = useCountUp(stats?.ai_qualified_total ?? 0);
  const buyers = useCountUp(stats?.buyers_live ?? 0);

  return (
    <div data-testid="trust-banner" className="border-y border-slate-200/80 bg-gradient-to-br from-white via-slate-50/40 to-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-7">
        <div className="flex items-center gap-3 mb-5">
          <span className="relative flex items-center justify-center size-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full size-2.5 bg-emerald-500" />
          </span>
          <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-slate-600">
            Live · platform telemetry · updated every 15s
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
          <Stat
            icon={<Activity className="size-4" strokeWidth={1.6} />}
            label="Enquiries routed · 24h"
            value={format(leads24h)}
            sub={stats ? `${format(stats.leads_last_30d)} in last 30d` : "—"}
            testid="trust-stat-24h"
          />
          <Stat
            icon={<Network className="size-4" strokeWidth={1.6} />}
            label="Total routed via Connexis"
            value={format(leadsTotal)}
            sub={`across ${stats?.sectors_covered ?? 10} financial sectors`}
            testid="trust-stat-total"
          />
          <Stat
            icon={<Cpu className="size-4" strokeWidth={1.6} />}
            label="AI-qualified conversations"
            value={format(qualified)}
            sub={stats ? `${format(stats.ai_sessions_total)} sessions total` : "—"}
            testid="trust-stat-ai"
          />
          <Stat
            icon={<ShieldCheck className="size-4" strokeWidth={1.6} />}
            label="Live buyer endpoints"
            value={format(buyers)}
            sub={`${stats?.ai_deployments ?? 0} active AI deployments`}
            testid="trust-stat-buyers"
          />
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, sub, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <span className="inline-grid place-items-center size-8 rounded-md bg-slate-900 text-white shrink-0 mt-0.5">{icon}</span>
      <div className="min-w-0">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500 leading-tight">{label}</div>
        <div className="mt-1 text-3xl sm:text-[34px] font-semibold tracking-[-0.03em] text-slate-900 tabular-nums leading-none">{value}</div>
        <div className="mt-1 text-xs text-slate-500 sm:truncate">{sub}</div>
      </div>
    </div>
  );
}
